import React, { useEffect, useMemo, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform, useSpring, useMotionValueEvent, type MotionValue } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { useSuppressFloatingCta } from './floatingCtaContext';
// Authentic four-color Slack mark, inlined as SVG so it never falls back to a
// flat monochrome CDN rendering.
function SlackLogo({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-4549" data-mp="1vpo5tjpra6j1">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-4550" data-mp="29onomaql7r7a" />

      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-4551" data-mp="bauhzfdgx22w" />

      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-4552" data-mp="3w23yyrcdxaju" />

      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-4553" data-mp="dnuq667aijrp" />
    </svg>;
}
// Microsoft Teams mark — the brand purple rounded square with the white "T"
// glyph, inlined so it always renders in full color (the simpleicons CDN only
// serves a flat monochrome version).
function TeamsLogo({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-4554" data-mp="2hd7s0c90hg72">
      <circle cx="18.1" cy="6" r="2.3" fill="#7B83EB" data-id="element-4555" data-mp="2dbvxptf706sj" />
      <path d="M22.8 11.4c0-.85-.69-1.54-1.54-1.54h-4.2c.42.62.66 1.36.66 2.16v4.3c0 .24-.03.48-.08.7h3.62c.85 0 1.54-.69 1.54-1.54z" fill="#7B83EB" data-id="element-4556" data-mp="2ddgwxwcgcdwq" />

      <circle cx="12.8" cy="6.5" r="2.95" fill="#5059C9" data-id="element-4557" data-mp="2mara5n8tyd4a" />
      <path d="M16.3 11.3c0-.94-.76-1.7-1.7-1.7h-3.6c-.94 0-1.7.76-1.7 1.7v4.5c0 1.6 1.3 2.9 2.9 2.9h1.2c1.6 0 2.9-1.3 2.9-2.9z" fill="#5059C9" data-id="element-4558" data-mp="30bk15xzxaq2n" />

      <rect x="1" y="5.6" width="12.4" height="12.4" rx="2.3" fill="#4B53BC" data-id="element-4559" data-mp="2w9703mw4fgc7" />
      <path d="M3.5 9h6.9v1.65H8.0v5.85H6.0V10.65H3.5z" fill="#fff" data-id="element-4560" data-mp="3a1mvc1is6a2k" />
    </svg>;
}
// Two platforms the chat panel can render as. Selecting one re-themes the
// accent color used for @mentions, channel chips and reactions, plus the
// sidebar tint and workspace name — so the toggle is genuinely functional,
// not decorative.
type PlatformId = 'teams' | 'slack';
const PLATFORMS: Record<PlatformId, {
  name: string;
  Logo: ({
    className
  }: {
    className?: string;
  }) => JSX.Element;
  accent: string;
  accentSoft: string;
  sidebarBg: string;
  workspace: string;
}> = {
  teams: {
    name: 'Teams',
    Logo: TeamsLogo,
    accent: '#5B5FC7',
    accentSoft: 'rgba(91,95,199,0.12)',
    sidebarBg: '#F5F4FB',
    workspace: 'Acme'
  },
  slack: {
    name: 'Slack',
    Logo: SlackLogo,
    accent: '#1264A3',
    accentSoft: 'rgba(18,100,163,0.10)',
    sidebarBg: '#F6F5F3',
    workspace: 'Acme'
  }
};
// Each scenario is a complete Faith → Giga exchange. The panel rotates through
// them so the section shows Giga handling genuinely different jobs — a sales
// renewal, a support triage, a finance roll-up — not one scripted demo on loop.
type Scenario = {
  // The workspace channel this exchange lives in (sales / support / finance).
  // Drives both the sidebar highlight and the conversation header so the panel
  // always reads as the channel matching the job Giga is doing.
  sidebarChannel: string;
  channel: string;
  // Microsoft Teams doesn't have #channels — it has named group chats. Each
  // scenario maps to a Teams chat so toggling to Teams swaps the sidebar/header
  // to the matching group chat instead of a channel.
  teamsChat: {
    id: string;
    name: string;
    emoji: string;
    tint: string;
    time: string;
    preview: string;
  };
  before: string;
  after: string;
  reply: React.ReactNode;
  // Optional rich deliverable Giga hands back inline — a generated document
  // (e.g. a PDF) previewed as a warm report card with a headline metric, a
  // mini bar chart, and a file pill. Makes the "Giga does the work and gives
  // you the finished file" beat tangible rather than text-only.
  card?: {
    title: string;
    meta: string;
    stat: string;
    statLabel: string;
    bars: number[];
    fileName: string;
    fileMeta: string;
  };
  tools: {
    name: string;
    icon: string;
    text: string;
  }[];
};
const SCENARIOS: Scenario[] = [{
  sidebarChannel: 'sales',
  channel: '#northwind',
  teamsChat: {
    id: 'sales',
    name: 'Sales Pipeline',
    emoji: '📈',
    tint: '#DCFCE7',
    time: '1:50 PM',
    preview: 'Faith: pull the latest from HubSpot…'
  },
  before: ' pull the latest from ',
  after: ' and build the renewal brief for the 2pm call',
  reply: <>
        Done — pulled the full Northwind account context and packaged the
        renewal brief as a PDF. ARR is up{' '}
        <span className="font-bold" data-id="element-4561" data-mp="ye1l46elpp96">+18% YoY</span>, and I flagged the open API
        risk <span className="font-bold" data-id="element-4562" data-mp="yfuqzjhe45g4">(ENG-402)</span> so you can lead with
        the extension offer. Ready for your 2pm.
      </>,
  card: {
    title: 'Northwind renewal brief',
    meta: 'Acme · Q3 renewal · 2pm call',
    stat: '+18%',
    statLabel: 'ARR YoY',
    bars: [42, 55, 48, 70, 62, 84, 76, 92, 88],
    fileName: 'Northwind-Renewal-Brief.pdf',
    fileMeta: '8 pages · PDF'
  },
  tools: [{
    name: 'HubSpot',
    icon: 'hubspot',
    text: 'Pull Northwind account history and renewal date'
  }, {
    name: 'Gmail',
    icon: 'gmail',
    text: 'Scan recent threads with Northwind stakeholders'
  }, {
    name: 'Notion',
    icon: 'notion',
    text: 'Draft renewal brief in the Q3 Renewals workspace'
  }, {
    name: 'Google Calendar',
    icon: 'googlecalendar',
    text: 'Check attendees for the 2pm call'
  }]
}, {
  sidebarChannel: 'support',
  channel: '#support',
  teamsChat: {
    id: 'support',
    name: 'Support Triage',
    emoji: '🎧',
    tint: '#DBEAFE',
    time: '1:50 PM',
    preview: 'Faith: triage the new tickets…'
  },
  before: ' triage the new tickets in ',
  after: ' and flag anything that looks like churn risk',
  reply: <>
        Triaged 14 new tickets — routed 9 to the right owners and grouped 3
        billing complaints from{' '}
        <span className="font-bold" data-id="element-4563" data-mp="3ddxpldqntoet">enterprise accounts</span> as churn risk. I
        looped in their CSMs and opened a Linear issue to track the fix.
      </>,
  tools: [{
    name: 'Zendesk',
    icon: 'zendesk',
    text: 'Read and categorize 14 new support tickets'
  }, {
    name: 'Salesforce',
    icon: 'salesforce',
    text: 'Match tickets to enterprise account tiers'
  }, {
    name: 'Linear',
    icon: 'linear',
    text: 'Open an ENG issue for the billing bug'
  }, {
    name: 'Slack',
    icon: 'slack',
    text: 'Loop in the CSMs for the at-risk accounts'
  }]
}, {
  sidebarChannel: 'finance',
  channel: '#finance',
  teamsChat: {
    id: 'finance',
    name: 'Weekly Metrics',
    emoji: '🔥',
    tint: '#FFEDD5',
    time: '8:01 AM',
    preview: 'Faith: roll up last month…'
  },
  before: ' roll up last month from ',
  after: ' and send the board a one-pager',
  reply: <>
        Closed the books for May: revenue up{' '}
        <span className="font-bold" data-id="element-4564" data-mp="1wndmbph64abi">12% MoM</span>, burn down 4%. I built the
        board one-pager with variance notes and dropped it in your Drive, ready
        for review.
      </>,
  tools: [{
    name: 'Stripe',
    icon: 'stripe',
    text: 'Pull May revenue and refunds'
  }, {
    name: 'QuickBooks',
    icon: 'quickbooks',
    text: 'Reconcile expenses and compute burn'
  }, {
    name: 'Google Sheets',
    icon: 'googlesheets',
    text: 'Build the variance breakdown'
  }, {
    name: 'Google Drive',
    icon: 'googledrive',
    text: 'Save the board one-pager for review'
  }]
}];
// The full set of workspace channels shown in the sidebar. The highlighted one
// follows the active scenario's `sidebarChannel`, so the selection (and the
// conversation header) always match the job Giga is currently doing — a sales
// exchange lights up #sales, a support triage lights up #support, etc.
const CHANNELS = ['general', 'sales', 'engineering', 'support', 'finance'];
// A static Teams group chat that always sits pinned at the top of the Teams
// "Chat" list, above the scenario-driven chats. Never the active conversation —
// it's there so the Teams sidebar reads as a real chat list, not a lone row.
const TEAMS_STATIC_PINNED = {
  id: 'general',
  name: 'General',
  emoji: '👋',
  tint: '#DCFCE7',
  time: '5:00 PM',
  preview: "Adam: Let's coordinate with everyone…"
};
type ChatPhase = 'sent' | 'thinking' | 'response';
// The white Slack-style chat panel, mirroring the feature page: a sent Faith
// message, Giga "thinking", then Giga's reply with the drafted brief and the
// sources it touched. Loops so the section is always alive.
// The Giga avatar inside the chat reply. Until `reveal` reaches 1 it shows as a
// neutral grey rounded square with NO logo — so the chat doesn't display the
// Giga logo before the bridge mark has landed. As `reveal` resolves, the brand
// orange fill and the Giga glyph fade in together, exactly where the mark
// settles, so it reads as the mark BECOMING the profile picture.
function GigaChatAvatar({
  innerRef,
  reveal
}: {
  innerRef?: React.Ref<HTMLDivElement>;
  reveal: MotionValue<number> | number;
}) {
  return <div ref={innerRef} className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden flex items-center justify-center shrink-0" data-id="element-4565" data-mp="2yeypzmbnzuii">
      {/* The avatar slot is COMPLETELY EMPTY (no fill, no border, no grey box)
                                                                                                                                         until the travelling mark lands. `reveal` is a hard step driven by the
                                                                                                                                         mark's landing, so the brand-orange avatar appears as a clean cut the
                                                                                                                                         instant the mark settles — it never fades in, and there is nothing in
                                                                                                                                         the slot beforehand. */}
      <motion.span aria-hidden="true" className="absolute inset-0 bg-[#EA580C]" style={{
      opacity: reveal
    }} data-id="element-4566" data-mp="1t7yu6o5jvkd7" />

      {/* The Giga glyph — appears with the fill on the same hard cut. */}
      <motion.img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="relative w-5 h-5 sm:w-6 sm:h-6 object-contain [filter:brightness(0)_invert(1)]" style={{
      opacity: reveal
    }} data-id="element-4567" data-mp="1jhciv64v9g0b" />
    </div>;
}
// The inline deliverable card Giga produces — a warm report header with a
// headline metric and a mini bar chart, plus a file pill underneath. Mirrors
// the workspace-hero result card so the "finished file" beat is consistent
// across the site. Animates in on its own beat after the reply text.
function ScenarioCard({
  card
}: {
  card: NonNullable<Scenario['card']>;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.98
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: 0.45,
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="mt-3 mb-3 w-full max-w-[420px] sm:max-w-[320px] overflow-hidden rounded-2xl border border-[#F1E6DA] bg-white shadow-[0_8px_24px_rgba(43,33,26,0.08)]" data-id="element-4568" data-mp="mqtvi3ud497x">
      {/* Warm report header with the headline metric and a mini bar chart.
       On MOBILE the header is locked to a 16:9 landscape page ratio (like
       the first slide of a deck or a PDF cover) so the deliverable reads as
       a real document rather than a tall square; desktop keeps its natural
       compact height. Kept intentionally soft — the surrounding section is
       already a strong orange, so a lighter peach gradient here reads warm
       without competing. */}
      <div className="relative flex aspect-[16/9] flex-col justify-between p-3.5 sm:aspect-auto sm:block bg-gradient-to-br from-[#FDF0E1] via-[#FADCB8] to-[#F3B87C] sm:p-4" data-id="element-4569" data-mp="jhb2msyi8n7">
        <div className="flex items-start justify-between gap-3" data-id="element-4570" data-mp="3nmm3x5ran85f">
          <div className="min-w-0" data-id="element-4571" data-mp="341u7gl2vdps0">
            <p className="truncate sm:whitespace-normal font-title text-[15px] sm:text-lg font-semibold leading-tight text-[#2B211A]" data-id="element-4572" data-mp="342wziwbxhy9i">
              {card.title}
            </p>
            <p className="truncate sm:whitespace-normal text-[10.5px] sm:text-[11px] font-medium text-[#2B211A]/60" data-id="element-4573" data-mp="3d31yqj6yxqcl">
              {card.meta}
            </p>
          </div>
          <div className="text-right shrink-0" data-id="element-4574" data-mp="3n0q40327ag90">
            <p className="font-title text-xl sm:text-2xl font-bold leading-none text-[#2B211A]" data-id="element-4575" data-mp="3mzf1e6khrejh">
              {card.stat}
            </p>
            <p className="text-[10px] font-medium text-[#2B211A]/60" data-id="element-4576" data-mp="3hzsnduwu9u9m">
              {card.statLabel}
            </p>
          </div>
        </div>
        <div className="flex h-9 sm:mt-4 sm:h-12 items-end gap-1" data-id="element-4577" data-mp="3rr6hto9xo3vw">
          {card.bars.map((h, i) => <motion.div key={i} className="flex-1 rounded-t-[3px] bg-white/55" initial={{
          height: 0
        }} animate={{
          height: `${h}%`
        }} transition={{
          delay: 0.6 + i * 0.05,
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-4578" data-mp="9l1wzls0ah7f" data-mpi={i} />)}
        </div>
      </div>
      {/* File pill. The badge is deliberately small: the file name is the
       information, and a large "PDF" tile was pulling focus off the report
       header above it. */}
      <div className="flex items-center gap-2.5 px-3.5 py-2" data-id="element-4579" data-mp="2cuvf9a4en15v">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#FAF3EC] text-[7.5px] font-bold text-[#EA580C]" data-id="element-4580" data-mp="2cwgeve5ktaal">
          PDF
        </span>
        <div className="flex min-w-0 items-baseline gap-2" data-id="element-4581" data-mp="1l3z4qg5th8lr">
          <p className="truncate text-[12.5px] font-semibold text-[#2B211A]" data-id="element-4582" data-mp="1p517v82uu4t0">
            {card.fileName}
          </p>
          <p className="shrink-0 text-[10.5px] text-neutral-400" data-id="element-4583" data-mp="1u4x9x1rzrm5x">
            {card.fileMeta}
          </p>
        </div>
      </div>
    </motion.div>;
}
// A quiet, single-row footnote listing the tools Giga touched — deliberately
// low-emphasis so it sits BENEATH the deliverable card as supporting evidence
// ("here's where I got it"), not as the headline of the reply. Each tool is a
// small monochrome brand mark + its name, separated visually by spacing,
// wrapping onto a second line if needed. This replaces the old four-line
// bulleted list of full sentences, which made the response read as bullet
// points of stuff rather than "Faith tagged Giga → Giga handed back the PDF".
function SourcesFootnote({
  tools
}: {
  tools: Scenario['tools'];
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.7,
    duration: 0.35
  }} className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-4584" data-mp="2rxdgww6p2vou">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400" data-id="element-4585" data-mp="2ix71v5vclnnk">
        Sources
      </span>
      {tools.map(tool => <span key={tool.name} className="inline-flex items-center gap-1.5" data-id="element-4586" data-mp="l6dz5yst99ki">
          <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tool.icon}.svg`} alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain opacity-80" loading="lazy" onError={e => {
        const img = e.currentTarget as HTMLImageElement;
        if (!img.dataset.fallback) {
          img.dataset.fallback = '1';
          img.src = `https://cdn.simpleicons.org/${tool.icon}`;
        } else {
          img.style.visibility = 'hidden';
        }
      }} data-id="element-4587" data-mp="h6dlwhhg74f1" />

          <span className="whitespace-nowrap text-[12px] sm:text-[13px] font-medium text-neutral-600" data-id="element-4588" data-mp="2te0vt3go1tub">
            {tool.name}
          </span>
        </span>)}
    </motion.div>;
}
export function SlackPanel({
  gigaRef,
  avatarReveal,
  responseReady = true,
  // Body height. Taller variants give the thread room to show a full exchange
  // without the closing beat sitting below the fold.
  heightClass = 'h-[468px] sm:h-[520px]',
  className = 'w-full max-w-[720px]'
}: {
  heightClass?: string;
  className?: string;
  gigaRef?: React.Ref<HTMLDivElement>; // 0 → the Giga avatar slot is a neutral grey placeholder (no logo yet);
  // 1 → it has resolved into the brand-orange avatar with the Giga logo. Driven
  // by the bridge mark's landing so the logo appears exactly as the mark settles.
  avatarReveal?: MotionValue<number> | number; // Gates the conversation: while false, the panel holds in its "thinking"
  // lead-in (avatar slot present so the bridge mark can land into it, but NO
  // response yet). Flips true once the Giga logo is fully in place, after which
  // the reply is produced and the scenario loop runs. Guarantees Giga never
  // "responds" behind a grey, logo-less avatar.
  responseReady?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const threadRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<ChatPhase>('sent'); // Which scenario is on screen. Advances by one each loop so the panel cycles
  // through the different jobs Giga can do.
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const scenario = SCENARIOS[scenarioIndex]; // Which messaging platform the panel renders as. The toggle in the top bar
  // flips this, re-theming the accent color, sidebar and workspace name.
  const [platform, setPlatform] = useState<PlatformId>('slack');
  const theme = PLATFORMS[platform]; // Teams renders its conversation differently from Slack: messages sit in
  // their own bubbled cards with more vertical breathing room, so the thread
  // reads as a more spread-out chat. We branch a few layout tokens on this.
  const isTeams = platform === 'teams';
  const scrollThreadToEnd = () => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  };
  useEffect(() => {
    if (reduceMotion) {
      setPhase('response');
      return;
    }
    // Until the Giga logo has landed in the avatar slot, HOLD in the "thinking"
    // lead-in: the avatar (grey → resolving) and typing dots are shown so the
    // bridge mark has a slot to settle into, but no results are generated yet.
    if (!responseReady) {
      setPhase('thinking');
      return;
    }
    const timers: number[] = [];
    // The logo is now in place. Resolve the current thinking lead-in into the
    // reply, then cycle through the other scenarios on a sent → thinking →
    // reply loop.
    timers.push(window.setTimeout(() => setPhase('response'), 650));
    const loop = window.setInterval(() => {
      setScenarioIndex(i => (i + 1) % SCENARIOS.length);
      setPhase('sent');
      timers.push(window.setTimeout(() => setPhase('thinking'), 700));
      timers.push(window.setTimeout(() => setPhase('response'), 1600));
    }, 7000);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(loop);
    };
  }, [reduceMotion, responseReady]);
  useEffect(() => {
    if (phase === 'response' || phase === 'thinking') {
      // Last delay lines up with Faith's 🙌 reaction (~1.5s) so the closing
      // beat scrolls into view instead of landing below the fold.
      const delays = [120, 400, 800, 1300, 1700];
      const timers = delays.map(d => window.setTimeout(scrollThreadToEnd, d));
      return () => timers.forEach(clearTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
  return <div className={className} data-id="element-4589" data-mp="35zv1zr94xb8u">
      <div className="relative bg-white rounded-2xl overflow-hidden border border-neutral-200" style={{
      boxShadow: '0 40px 90px -25px rgba(43,33,26,0.45)'
    }} data-id="element-4590" data-mp="360o0y61uqwgf">
        {/* Top bar — two zones that line up with the sidebar / conversation
         split below: a "Workspace" identity over the sidebar, and the active
         channel plus the platform toggle over the conversation. */}
        <div className="flex items-stretch border-b border-neutral-100 bg-white" data-id="element-4591" data-mp="3oxfquybwg9tg">
          {/* Left zone — sits over the sidebar column. Slack reads as a
           "Workspace" switcher; Teams reads as the "Chat" list title. */}
          <div className="hidden sm:flex w-40 shrink-0 items-center gap-1 px-4 py-3.5 border-r border-neutral-200/70" data-id="element-4592" data-mp="khwdzphw1co3">
            <span className="font-bold text-[#2B211A] text-sm sm:text-[15px] truncate" data-id="element-4593" data-mp="285aiiqigvue1">
              {isTeams ? 'Chat' : 'Workspace'}
            </span>
            {!isTeams && <ChevronDown size={15} className="text-neutral-400 shrink-0" data-id="element-4594" data-mp="2c99x33k6sj4r" />}
          </div>
          {/* Right zone — sits over the conversation column. */}
          <div className="flex-1 flex items-center justify-between gap-3 px-4 sm:px-5 py-3 min-w-0" data-id="element-4595" data-mp="2m4a8q2lmohcb">
            {/* Conversation header follows the active scenario. Slack shows a
             "# channel" title; Teams shows the group-chat name with its round
             emoji avatar (no #, no dropdown) — Teams has chats, not channels. */}
            <div className="flex items-center gap-2 min-w-0 shrink-0" data-id="element-4596" data-mp="2zyufnlcfhedi">
              {isTeams ? <AnimatePresence mode="wait" data-id="element-4597" data-mp="34xe2dnyuiu6x">
                  <motion.div key={scenario.teamsChat.id} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="flex items-center gap-2 min-w-0" data-id="element-4598" data-mp="39uo15u84ki7f">
                    <span className="hidden sm:flex items-center justify-center w-6 h-6 rounded-full text-[13px] shrink-0" style={{
                  backgroundColor: scenario.teamsChat.tint
                }} data-id="element-4599" data-mp="1jjj4z2z4puhq">
                      {scenario.teamsChat.emoji}
                    </span>
                    <span className="font-bold text-[#2B211A] text-sm sm:text-[15px] truncate" data-id="element-4600" data-mp="1oic9w70cod6a">
                      {scenario.teamsChat.name}
                    </span>
                  </motion.div>
                </AnimatePresence> : <>
                  <span className="text-neutral-400 font-medium shrink-0" data-id="element-4601" data-mp="1xj208v6udbcm">
                    #
                  </span>
                  {/* The channel name follows the active scenario so the header
                   always matches the job on screen. It crossfades as it cycles.
                   It must never truncate — the words ("finance", "support") are
                   short, so we let it size naturally and keep the toggle from
                   squeezing it on mobile. */}
                  <span className="font-bold text-[#2B211A] text-sm sm:text-[15px] whitespace-nowrap" data-id="element-4602" data-mp="2518kakxobfnv">
                    <AnimatePresence mode="wait" data-id="element-4603" data-mp="29z1rnhapqfpq">
                      <motion.span key={scenario.sidebarChannel} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="inline-block" data-id="element-4604" data-mp="1w0k5vp4dbdag">
                        {scenario.sidebarChannel}
                      </motion.span>
                    </AnimatePresence>
                  </span>
                  <ChevronDown size={15} className="text-neutral-400 shrink-0" data-id="element-4605" data-mp="3lfrt6n46yjd0" />
                </>}
            </div>

            {/* Platform toggle — teams ↔ Slack. Functional: re-themes the
             whole panel. Styled like liquid glass: a frosted translucent
             track with a glossy, light-bending pill that slides between
             options. */}
            <div className="relative grid grid-cols-2 items-center rounded-full p-1 shrink-0 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_1px_2px_rgba(43,33,26,0.08)] [backdrop-filter:blur(12px)_saturate(160%)] [-webkit-backdrop-filter:blur(12px)_saturate(160%)]" style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.55), rgba(244,240,234,0.35))'
          }} data-id="element-4606" data-mp="1cf6skxge3j1m">
              {/* A single sliding pill, animated ONLY when the platform actually
               changes. It is NOT keyed to layout/render (no layoutId), so the
               panel can re-render every scenario cycle without the pill ever
               nudging itself to "stay in place." */}
              <motion.span aria-hidden="true" className="pointer-events-none absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full overflow-hidden border border-white/80 [backdrop-filter:blur(8px)] [-webkit-backdrop-filter:blur(8px)]" style={{
              background: 'linear-gradient(150deg, rgba(255,255,255,0.95), rgba(255,255,255,0.7))',
              boxShadow: 'inset 0 1px 1.5px rgba(255,255,255,1), inset 0 -1px 2px rgba(43,33,26,0.06), 0 2px 6px rgba(43,33,26,0.16)'
            }} animate={{
              x: platform === 'teams' ? '0%' : '100%'
            }} transition={{
              type: 'spring',
              stiffness: 420,
              damping: 34
            }} data-id="element-4607" data-mp="ereqpq6dztnp">
                {/* Specular sheen sweeping across the glass pill */}
                <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full" style={{
                background: 'radial-gradient(120% 80% at 25% 12%, rgba(255,255,255,0.9), rgba(255,255,255,0) 55%)'
              }} data-id="element-4608" data-mp="lcuv2l2ti5h" />
              </motion.span>
              {(['teams', 'slack'] as const).map(p => {
              const active = platform === p;
              const P = PLATFORMS[p];
              return <button key={p} type="button" onClick={() => setPlatform(p)} aria-pressed={active} className={`relative z-10 flex items-center justify-center gap-1.5 rounded-full px-2.5 sm:px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${active ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-4609" data-mp="2r71d87b9e2l6">
                    <P.Logo className="relative w-4 h-4 shrink-0" data-id="element-4610" data-mp="28ahz13lllohr" />
                    <span className="relative whitespace-nowrap" data-id="element-4611" data-mp="28ccmopo97upj">
                      {/* On mobile the toggle is tight, so drop the "MS "
                       prefix and just read "Teams"; full name on >= sm. */}
                      {p === 'teams' ? <>
                          <span className="sm:hidden" data-id="element-4612" data-mp="2fys5j3jwbl1">Teams</span>
                          <span className="hidden sm:inline" data-id="element-4613" data-mp="3tnuqhcm9f9dx">{P.name}</span>
                        </> : P.name}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </div>

        <div className={`flex ${heightClass}`} data-id="element-4614" data-mp="a1u8b9r4ilr9">
          {/* Sidebar */}
          <div className="hidden sm:flex w-40 shrink-0 text-neutral-600 border-r border-neutral-200/70 flex-col py-4 transition-colors duration-300" style={{
          backgroundColor: theme.sidebarBg
        }} data-id="element-4615" data-mp="3s6p4fdo38clr">
            {isTeams ?
          // ── MICROSOFT TEAMS ── A "Chat" list of named group chats
          // (Pinned + Recent), each with an emoji avatar, name, time, and a
          // last-message preview — Teams has chats, not #channels. The
          // pinned scenario chats highlight to match the active exchange.
          <>
                <div className="px-4 mb-1.5 flex items-center gap-1.5 text-[12px] font-bold text-neutral-500" data-id="element-4616" data-mp="izxveahrbql6">
                  <ChevronDown size={13} className="text-neutral-400" data-id="element-4617" data-mp="16ps2scjb4my1" />
                  Pinned
                </div>
                <div className="flex flex-col" data-id="element-4618" data-mp="1bjmp7nqarg0t">
                  {[TEAMS_STATIC_PINNED, ...SCENARIOS.map(s => s.teamsChat)].map(chat => {
                const active = chat.id === scenario.teamsChat.id;
                return <div key={chat.id} className={`relative flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${active ? '' : 'hover:bg-black/[0.04]'}`} data-id="element-4619" data-mp="d1cbtmulxny0">
                        {active && <motion.div layoutId="channel-active-pill" className="absolute inset-0 bg-[#2B211A]/[0.06]" transition={{
                    type: 'spring',
                    stiffness: 480,
                    damping: 38
                  }} data-id="element-4620" data-mp="80esv81rwpvg" />}
                        <span className="relative flex items-center justify-center w-7 h-7 rounded-full text-[13px] shrink-0" style={{
                    backgroundColor: chat.tint
                  }} data-id="element-4621" data-mp="vqitd6jo09i8">
                          {chat.emoji}
                        </span>
                        <span className="relative flex-1 min-w-0" data-id="element-4622" data-mp="3biovwp78dslm">
                          <span className="flex items-center justify-between gap-1" data-id="element-4623" data-mp="1r0xkjkho9ak8">
                            <span className={`text-[13px] truncate ${active ? 'font-bold text-[#2B211A]' : 'font-semibold text-[#2B211A]'}`} data-id="element-4624" data-mp="1qyun4fe6wf5h">
                              {chat.name}
                            </span>
                            <span className="text-[10px] text-neutral-400 shrink-0" data-id="element-4625" data-mp="25ptq9aw9abho">
                              {chat.time}
                            </span>
                          </span>
                          <span className="block text-[11px] text-neutral-400 truncate" data-id="element-4626" data-mp="2eqi5g8xdyhw5">
                            {chat.preview}
                          </span>
                        </span>
                      </div>;
              })}
                </div>

                <div className="px-4 mt-3 mb-1.5 flex items-center gap-1.5 text-[12px] font-bold text-neutral-500" data-id="element-4627" data-mp="2oo60z12tftfd">
                  <ChevronDown size={13} className="text-neutral-400" data-id="element-4628" data-mp="pdof5pbsyy3s" />
                  Recent
                </div>
                {/* Shimmer rows — the rest of the (still-loading) recent chats. */}
                <div className="flex flex-col gap-2.5 px-3 mt-1" data-id="element-4629" data-mp="fudla8pcly4i">
                  {[0.9, 0.7, 0.85, 0.55, 0.45].map((w, i) => <div key={i} className="flex items-center gap-2.5" style={{
                opacity: 1 - i * 0.14
              }} data-id="element-4630" data-mp="kozzejmte4sa" data-mpi={i}>
                      <div className="w-7 h-7 rounded-full bg-neutral-200/80 shimmer-block shrink-0" data-id="element-4631" data-mp="awkgnk3p1c6c" />
                      <div className="h-3.5 rounded-md bg-neutral-200/80 shimmer-block" style={{
                  width: `${w * 100}%`
                }} data-id="element-4632" data-mp="ymof7d20ylqh" />
                    </div>)}
                </div>
              </> :
          // ── SLACK ── The classic "# channel" list. The highlighted
          // channel follows the active scenario.
          <>
                <button type="button" className="px-4 mb-2 flex items-center gap-1.5 text-[13px] font-bold text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-4633" data-mp="tpocnc4dw144">
                  <ChevronDown size={14} className="text-neutral-400" data-id="element-4634" data-mp="3efnhhcq4tu2i" />
                  Channels
                </button>
                <div className="flex flex-col text-[13px] font-medium" data-id="element-4635" data-mp="1wik2gdm51ace">
                  {CHANNELS.map(channel => {
                const active = channel === scenario.sidebarChannel;
                return <div key={channel} className={`relative px-4 py-1.5 cursor-pointer transition-colors ${active ? 'text-[#2B211A] font-bold' : 'hover:bg-black/[0.04]'}`} data-id="element-4636" data-mp="25gchdgny6fz9">
                        {active && <motion.div layoutId="channel-active-pill" className="absolute inset-0 bg-[#2B211A]/[0.06]" transition={{
                    type: 'spring',
                    stiffness: 480,
                    damping: 38
                  }} data-id="element-4637" data-mp="1dqhfylmtezu8" />}
                        <span className="relative" data-id="element-4638" data-mp="1hr1e9sj4k9hy"># {channel}</span>
                      </div>;
              })}
                </div>

                {/* Shimmer placeholders — the rest of the (unloaded) sidebar,
                 fading out toward the bottom so the panel feels like a live,
                 still-loading workspace. */}
                <div className="mt-2 flex flex-col gap-2.5 px-4" data-id="element-4639" data-mp="2p4lxa9ypxn2v">
                  {[0.9, 0.9, 0.9, 0.6, 0.85, 0.5, 0.45].map((w, i) => <div key={i} className="flex items-center gap-2.5" style={{
                opacity: 1 - i * 0.12
              }} data-id="element-4640" data-mp="2u259737y1nrn" data-mpi={i}>
                      <div className="w-5 h-5 rounded-md bg-neutral-200/80 shimmer-block shrink-0" data-id="element-4641" data-mp="33ukti76pqofp" />
                      <div className="h-3.5 rounded-md bg-neutral-200/80 shimmer-block" style={{
                  width: `${w * 100}%`
                }} data-id="element-4642" data-mp="266ssf24jb32u" />
                    </div>)}
                </div>
              </>}
          </div>

          {/* Conversation */}
          <div className="flex-1 flex flex-col bg-white min-w-0" data-id="element-4643" data-mp="2g6th73l6abef">
            <div className="px-5 py-3.5 border-b border-neutral-100 flex items-center justify-between" data-id="element-4644" data-mp="2l4bjib4opsdg">
              <div className="flex items-center gap-2" data-id="element-4645" data-mp="3rj3zgg2aip4m">
                <div className="w-2.5 h-2.5 rounded-full bg-[#F97316]" data-id="element-4646" data-mp="ct5t4zm0qnb" />
                <span className="font-bold text-[#2B211A] text-sm sm:text-base" data-id="element-4647" data-mp="f5mletmsuys">
                  Giga
                </span>
                <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-4648" data-mp="1fvqsdl4flv98">
                  APP
                </span>
              </div>
              <AnimatePresence mode="wait" data-id="element-4649" data-mp="166gybzq122ih">
                <motion.span key={theme.workspace} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="text-xs sm:text-sm text-neutral-400" data-id="element-4650" data-mp="165nzdkxb8haw">
                  {theme.workspace}
                </motion.span>
              </AnimatePresence>
            </div>

            <div ref={threadRef} className="flex-1 p-5 overflow-y-auto relative scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-4651" data-mp="n9xw7udn5evf">
              <div className={`flex flex-col ${isTeams ? 'gap-7 sm:gap-9' : 'gap-5 sm:gap-6'}`} data-id="element-4652" data-mp="2hmnqwebu2s3v">
                {/* Faith's sent message. In Teams the sender (Faith, the
                 person typing) sits on the RIGHT in a purple accent bubble with
                 no avatar — matching the real Teams layout where your own
                 messages are right-aligned and purple. Slack keeps the classic
                 left-aligned avatar + name + plain text. */}
                <div className={`flex gap-3 sm:gap-4 ${isTeams ? 'justify-end' : ''}`} data-id="element-4653" data-mp="vkh2zx0ydlcb">
                  {!isTeams && <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/ChatGPT_Image_Apr_24,_2026,_12_02_55_PM_3.webp" alt="Faith" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-cover shrink-0" data-id="element-4654" data-mp="10ap6ny3y6hrp" />}
                  <div className={isTeams ? 'min-w-0 max-w-[85%] rounded-2xl rounded-br-md border px-4 py-3 shadow-sm' : 'min-w-0 flex-1'} style={isTeams ? {
                  // Your own (sent) message in Teams uses the accent
                  // lavender bubble; the recipient (Giga) keeps the
                  // neutral grey bubble below.
                  backgroundColor: theme.accentSoft,
                  borderColor: 'rgba(91,95,199,0.22)'
                } : undefined} data-id="element-4655" data-mp="clmp0q9bcox7">
                    {!isTeams && <div className="flex items-baseline gap-2 mb-1" data-id="element-4656" data-mp="2xqxk1bkxajl">
                        <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-4657" data-mp="2zttf1zexxw8">
                          Faith
                        </span>
                        <span className="text-xs text-neutral-400" data-id="element-4658" data-mp="cwq3hc4yms2w">
                          1:50 PM
                        </span>
                      </div>}
                    {/* The prompt itself crossfades on every scenario change, so
                     it's visually obvious the panel is cycling through
                     genuinely different jobs — not replaying one script. */}
                    <AnimatePresence mode="wait" data-id="element-4659" data-mp="vmy1xgxpsucr">
                      <motion.p key={scenarioIndex} initial={{
                      opacity: 0,
                      filter: 'blur(4px)'
                    }} animate={{
                      opacity: 1,
                      filter: 'blur(0px)'
                    }} exit={{
                      opacity: 0,
                      filter: 'blur(4px)'
                    }} transition={{
                      duration: 0.4,
                      ease: 'easeOut'
                    }} className="text-sm sm:text-base leading-relaxed text-neutral-700" data-id="element-4660" data-mp="vo93rb7lns18">
                        <span className="rounded px-1 py-0.5 font-medium" style={{
                        color: theme.accent,
                        backgroundColor: theme.accentSoft
                      }} data-id="element-4661" data-mp="1qefekq02ec0f">
                          @Giga
                        </span>
                        {scenario.before}
                        <span className="rounded px-1 py-0.5 font-medium" style={{
                        color: theme.accent,
                        backgroundColor: theme.accentSoft
                      }} data-id="element-4662" data-mp="11kb6apk7qdk">
                          {scenario.channel}
                        </span>
                        {scenario.after}
                      </motion.p>
                    </AnimatePresence>

                    {/* Giga acknowledges the moment it sees the request by
                     dropping a 👀 reaction on Faith's message — the same way
                     a teammate would signal "on it" before replying. */}
                    <AnimatePresence data-id="element-4663" data-mp="hcm74czjbnv2">
                      {phase !== 'sent' && <motion.div key="eyes-reaction" initial={{
                      opacity: 0,
                      scale: 0.6,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      scale: 0.6
                    }} transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 22
                    }} className="mt-2 hidden sm:inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5" style={{
                      borderColor: theme.accent + '4D',
                      backgroundColor: theme.accentSoft
                    }} data-id="element-4664" data-mp="cgnf8fezznpw">
                          <span className="text-sm leading-none" data-id="element-4665" data-mp="37xr6b5l1ou1h">👀</span>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Giga thinking / response */}
                <AnimatePresence mode="wait" data-id="element-4666" data-mp="4i3jsdkghh5o">
                  {phase === 'thinking' && <motion.div key="thinking" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="flex gap-3 sm:gap-4" data-id="element-4667" data-mp="3mt751nwkle47">
                      <GigaChatAvatar innerRef={gigaRef} reveal={avatarReveal ?? 1} data-id="element-4668" data-mp="n68k1b1wmjzq" />

                      {/* The Giga reply (name row + "Pulling context…") must NOT
                       appear until the flying Giga mark has actually landed in
                       the avatar slot. Before that (responseReady === false) we
                       render the avatar slot ALONE so the mark has somewhere to
                       settle, with no premature text beside a logo-less avatar. */}
                      {responseReady && <div className={`min-w-0 flex-1 ${isTeams ? 'rounded-2xl rounded-tl-md bg-[#F3F3F5] border border-neutral-200/70 px-4 py-3' : ''}`} data-id="element-4669" data-mp="1oxi5eiy0mkjg">
                          <div className="flex items-baseline gap-2 mb-1" data-id="element-4670" data-mp="1k11jzwi7aka2">
                            <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-4671" data-mp="1yxsk67zp800q">
                              Giga
                            </span>
                            <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-4672" data-mp="3aauxmu6i49go">
                              APP
                            </span>
                            <span className="text-xs text-neutral-400" data-id="element-4673" data-mp="zzkmeky2rjsg">
                              1:50 PM
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-2" data-id="element-4674" data-mp="18sei1sxtwvsh">
                            {[0, 0.2, 0.4].map(delay => <motion.div key={delay} className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                        y: [0, -3, 0]
                      }} transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay
                      }} data-id="element-4675" data-mp="14qt2fztwu3el" />)}
                            <span className="text-xs text-neutral-500 ml-1" data-id="element-4676" data-mp="q2zik2xskeq7">
                              Pulling context from your stack...
                            </span>
                          </div>
                        </div>}
                    </motion.div>}
                  {phase === 'response' && <motion.div key="response" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} onAnimationComplete={scrollThreadToEnd} className="flex gap-3 sm:gap-4" data-id="element-4677" data-mp="1wirzyqmp9e9c">
                      <GigaChatAvatar innerRef={gigaRef} reveal={avatarReveal ?? 1} data-id="element-4678" data-mp="291xiexirfukt" />

                      <div className={`min-w-0 flex-1 ${isTeams ? 'rounded-2xl rounded-tl-md bg-[#F3F3F5] border border-neutral-200/70 px-4 py-3' : ''}`} data-id="element-4679" data-mp="1v2e8bz6e295v">
                        <div className="flex items-baseline gap-2 mb-1" data-id="element-4680" data-mp="2noyzayiycj8w">
                          <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-4681" data-mp="2nruysrag7fzg">
                            Giga
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-4682" data-mp="2dyw7v2rhbdco">
                            APP
                          </span>
                          <span className="text-xs text-neutral-400" data-id="element-4683" data-mp="36nbk5hj87hkb">
                            1:50 PM
                          </span>
                        </div>

                        <div className="mt-1" data-id="element-4684" data-mp="3lathfuugl9wp">
                          <motion.div initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} transition={{
                        delay: 0.1,
                        duration: 0.4
                      }} className="text-sm sm:text-[15px] text-neutral-800 leading-relaxed mb-3" data-id="element-4685" data-mp="3lajsmal5h8sl">
                            {scenario.reply}
                          </motion.div>

                          {/* The finished deliverable Giga hands back — a
                           generated PDF previewed inline — is the HERO of the
                           reply: it leads, right under the message, so the
                           recognizable "Faith asked → here's your document"
                           moment lands first. Only some scenarios produce a
                           file, so render it only when present. */}
                          {scenario.card && <ScenarioCard card={scenario.card} data-id="element-4686" data-mp="2eozb1y1ik9qr" />}

                          {/* Sources Giga touched — DEMOTED to a quiet, compact
                           secondary line under the deliverable so the response
                           reads as "tagged Giga → got the PDF", not a wall of
                           bullet points. Rendered as small monochrome app marks
                           with their names on a single wrapping row, the way a
                           real app message footnotes the tools it used. */}
                          <SourcesFootnote tools={scenario.tools} data-id="element-4687" data-mp="fewqkxd2766c" />
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* Composer — styled to read like each real product so it feels
             familiar. The user always talks to Giga, so the placeholder reads
             "Ask Giga…" in both. Slack: a bordered rounded box with a small
             square Slack-green send button (paper-plane). Teams: a rounded box
             with a purple round send button. */}
            <div className="px-4 pt-4 pb-5 sm:p-5" data-id="element-4688" data-mp="1udapedac2y9j">
              <div className={`bg-white flex items-center gap-2 transition-all duration-300 ${isTeams ? 'rounded-[10px] border border-neutral-300 px-4 py-3 shadow-sm' : 'rounded-lg border border-neutral-300 px-3.5 py-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'}`} data-id="element-4689" data-mp="1uels09s1lzz2">
                <span className="text-sm sm:text-base flex-1 min-w-0 text-neutral-400 truncate" data-id="element-4690" data-mp="2i1ael8rzygm0">
                  Ask Giga…
                </span>
                <button type="button" aria-label="Ask Giga" className={`flex items-center justify-center shrink-0 transition-colors duration-300 ${isTeams ? 'w-9 h-9 rounded-full text-white' : 'w-7 h-7 sm:w-8 sm:h-8 rounded-md text-white'}`} style={{
                backgroundColor: isTeams ? theme.accent : '#007a5a'
              }} data-id="element-4691" data-mp="2d5mpsuhffph1">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 -ml-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4692" data-mp="snx9vifz2qkd">
                    <path d="m22 2-7 20-4-9-9-4Z" data-id="element-4693" data-mp="ilunwulfsfmj" />
                    <path d="M22 2 11 13" data-id="element-4694" data-mp="mjsarqrqipmr" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ─────────────────────────────────────────────────────────────────────────
// NEURAL SPHERE — "connects to your world", rendered as a 2D knowledge graph.
// Points sit on the surface of a sphere (so the silhouette stays perfectly
// round and reads "almost 3D"), but everything is drawn flat in a single SVG:
// thin synapse edges weave a mesh between neighbours, faint dot-nodes vary in
// size and brightness by depth, and pulses of light travel the mesh toward the
// Giga core — an "AI brain" intelligence look. No logos, no glass tiles.
// ─────────────────────────────────────────────────────────────────────────
export type BrainNode = {
  // Base position on the unit sphere.
  bx: number;
  by: number;
  bz: number;
  baseSize: number;
  hub: boolean;
  // Hub nodes carry a brand-logo slug so the pulsing orange hubs read as the
  // actual tools the inbound packets are flowing from.
  logo?: string;
  twinklePhase: number;
  driftPhase: number;
};
// Brand marks assigned to the hub nodes, so each pulsing hub looks like a real
// tool the data is streaming in from. These are dashboard-icons slugs (full,
// authentic brand logos), ordered by the most popular / relevant tools a Giga
// user would actually connect.
const HUB_LOGOS = ['slack', 'gmail', 'notion', 'salesforce', 'hubspot', 'google-drive', 'linear', 'github', 'microsoft-teams', 'jira', 'zoom', 'stripe', 'figma', 'asana', 'google-calendar', 'zendesk'];
export type Edge = {
  a: number;
  b: number;
  // A stable phase so each synapse fires its travelling pulse on its own beat.
  pulsePhase: number;
  // Length on the unit sphere — shorter edges (true neighbours) fire pulses;
  // longer struts only ever read as faint structure.
  short: boolean;
};
// More nodes = denser, more "AI brain"-like synaptic web. Bumped up so the
// mesh reads as a busy knowledge graph of dots + connections rather than a
// sparse constellation.
const NODE_COUNT = 280;
const RADIUS = 318;
const FOCAL = 640;
// Build the brain: Fibonacci-distributed points on a sphere (even coverage, no
// clumping) + a proximity mesh so every node links to its nearest neighbours,
// which is what gives the "synaptic web" / knowledge-graph texture.
export function buildBrain(): {
  nodes: BrainNode[];
  edges: Edge[];
} {
  const n = NODE_COUNT;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const nodes: BrainNode[] = [];
  for (let i = 0; i < n; i++) {
    const y = 1 - i / (n - 1) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = i * goldenAngle;
    // A scattering of larger "hub" nodes (every ~9th) reads as the busy,
    // high-degree nodes in a real knowledge graph.
    const hub = i % 9 === 0;
    const hubIndex = Math.floor(i / 9);
    nodes.push({
      bx: Math.cos(theta) * r,
      by: y,
      bz: Math.sin(theta) * r,
      baseSize: hub ? 4.5 + i * 7 % 4 : 1.6 + i * 5 % 4 * 0.5,
      hub,
      logo: hub ? HUB_LOGOS[hubIndex % HUB_LOGOS.length] : undefined,
      twinklePhase: i * 0.37 % 1,
      driftPhase: i * 0.61 % 1
    });
  }
  // Proximity edges: connect each node to its k nearest neighbours by 3D
  // distance. Deduped so each undirected edge appears once.
  const edges: Edge[] = [];
  const seen = new Set<string>();
  // Link each node to more nearest neighbours → a denser, more interconnected
  // synaptic web (more "connections and dots", per the brain metaphor).
  const K = 4;
  for (let i = 0; i < n; i++) {
    const dists: {
      j: number;
      d: number;
    }[] = [];
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const dx = nodes[i].bx - nodes[j].bx;
      const dy = nodes[i].by - nodes[j].by;
      const dz = nodes[i].bz - nodes[j].bz;
      dists.push({
        j,
        d: dx * dx + dy * dy + dz * dz
      });
    }
    dists.sort((p, q) => p.d - q.d);
    for (let k = 0; k < K; k++) {
      const j = dists[k].j;
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push({
        a: Math.min(i, j),
        b: Math.max(i, j),
        pulsePhase: edges.length * 0.293 % 1,
        short: dists[k].d < 0.06
      });
    }
  }
  return {
    nodes,
    edges
  };
}
// Project a unit-sphere point through the current rotation into 2D screen
// space, returning pixel offset from center, plus a 0→1 "front" depth used for
// brightness/size so the flat graph still reads as a rounded volume.
function project(node: BrainNode, rot: number, t: number) {
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const x = node.bx * cos - node.bz * sin;
  const z = node.bx * sin + node.bz * cos;
  const y = node.by;
  const depth = FOCAL / (FOCAL - z * RADIUS);
  const drift = Math.sin(t * 0.9 + node.driftPhase * Math.PI * 2) * 1.6;
  const px = x * RADIUS * depth;
  const py = y * RADIUS * depth + drift;
  const front = (z + 1) / 2;
  return {
    px,
    py,
    front
  };
}
const PULSE_CYCLE = 3.4; // seconds for a synapse pulse to traverse an edge
// ─────────────────────────────────────────────────────────────────────────
// LOGO STRIP — the neural sphere reads as abstract "AI intelligence", so the
// concrete brand logos that plug into Giga now live in a tasteful marquee
// directly beneath it. This answers "where do we show the tools?" without
// cluttering the brain visual: the sphere is the metaphor, the strip is the
// proof. Curated to slugs that render as reliable full-color marks.
// ─────────────────────────────────────────────────────────────────────────
// Each entry carries BOTH icon-source slugs so we can fall back cleanly:
//  - `simple`: simpleicons slug (no dashes) → full brand-color SVG, the most
//    consistently available CDN. This is the PRIMARY source.
//  - `dash`: dashboard-icons slug (homarr-labs / walkxcode) used as a backup.
// A brand-colored letter tile is the guaranteed final fallback, so no tile is
// ever blank. Curated to popular, relevant tools that all resolve on simpleicons.
// The "Connects to everything" strip. Slack / Teams are intentionally NOT here
// — they're the dedicated "@mention Giga in chat" surface, not a generic
// integration. Every tile keeps the brown espresso surface for a consistent,
// premium row. `mono` flags a logo whose real mark is black/dark (low contrast
// on espresso) — it's rendered as a clean WHITE glyph so it pops, exactly like
// Notion, instead of vanishing into the background.
const STRIP_LOGOS: {
  name: string;
  simple: string;
  dash: string;
  mono?: boolean;
}[] = [{
  name: 'Gmail',
  simple: 'gmail',
  dash: 'gmail'
}, {
  name: 'Google Drive',
  simple: 'googledrive',
  dash: 'google-drive'
}, {
  name: 'Google Calendar',
  simple: 'googlecalendar',
  dash: 'google-calendar'
}, {
  name: 'Notion',
  simple: 'notion',
  dash: 'notion',
  mono: true
}, {
  name: 'Asana',
  simple: 'asana',
  dash: 'asana'
}, {
  name: 'Monday',
  simple: 'mondaydotcom',
  dash: 'monday'
}, {
  name: 'Linear',
  simple: 'linear',
  dash: 'linear',
  mono: true
}, {
  name: 'Salesforce',
  simple: 'salesforce',
  dash: 'salesforce'
}, {
  name: 'HubSpot',
  simple: 'hubspot',
  dash: 'hubspot'
}, {
  name: 'Figma',
  simple: 'figma',
  dash: 'figma'
}, {
  name: 'Slack',
  simple: 'slack',
  dash: 'slack'
}, {
  name: 'Zapier',
  simple: 'zapier',
  dash: 'zapier',
  mono: true
}, {
  name: 'Shopify',
  simple: 'shopify',
  dash: 'shopify'
}, {
  name: 'Zoom',
  simple: 'zoom',
  dash: 'zoom'
}, {
  name: 'Mailchimp',
  simple: 'mailchimp',
  dash: 'mailchimp',
  mono: true
}, {
  name: 'Stripe',
  simple: 'stripe',
  dash: 'stripe'
}, {
  name: 'Airtable',
  simple: 'airtable',
  dash: 'airtable'
}, {
  name: 'Jira',
  simple: 'jira',
  dash: 'jira'
}, {
  name: 'Dropbox',
  simple: 'dropbox',
  dash: 'dropbox'
}, {
  name: 'Intercom',
  simple: 'intercom',
  dash: 'intercom',
  mono: true
}];
function StripLogo({
  logo
}: {
  logo: {
    name: string;
    simple: string;
    dash: string;
    mono?: boolean;
  };
}) {
  // White tiles: low-contrast / dark brand marks (Notion, Zapier, Linear,
  // Intercom, …) are rendered as a DARK simpleicons glyph so they stay visible
  // on white instead of vanishing the way a white glyph would.
  const sources = useMemo(() => logo.mono ? [`https://cdn.simpleicons.org/${logo.simple}/2B211A`] : [`https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/${logo.dash}.svg`, `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${logo.dash}.svg`, `https://cdn.simpleicons.org/${logo.simple}`], [logo.simple, logo.dash, logo.mono]);
  const [srcIndex, setSrcIndex] = useState(0);
  const failed = srcIndex >= sources.length;
  const advance = () => setSrcIndex(i => i + 1);
  return <div className="relative shrink-0 flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-2xl sm:rounded-3xl overflow-hidden bg-white border border-[#EFE9E1] shadow-lg shadow-black/5" title={logo.name} data-id="element-4695" data-mp="1srsq76rxa8m9">
      {failed ? <span className="relative flex h-10 w-10 items-center justify-center rounded-lg text-xl font-bold text-[#2B211A]" data-id="element-4696" data-mp="21q4daytkyo9y">
          {logo.name.charAt(0)}
        </span> : <img key={srcIndex} src={sources[srcIndex]} alt={logo.name} className="relative w-10 h-10 sm:w-12 sm:h-12 object-contain" loading="lazy" onError={advance} onLoad={e => {
      const img = e.currentTarget;
      if (img.naturalWidth === 0 || img.naturalHeight === 0) advance();
    }} data-id="element-4697" data-mp="2bqesgoz6e5rr" />}
    </div>;
}
export function LogoStrip({
  reduceMotion,
  theme = 'dark'
}: {
  reduceMotion: boolean;
  theme?: 'dark' | 'light';
}) {
  return <div className="w-full max-w-[1000px] flex flex-col items-center gap-6 sm:gap-10" data-id="element-4698" data-mp="2uhxscmxwl3pk">
      {theme === 'dark' && <span className="text-[11px] font-semibold uppercase tracking-[5px] text-white/45" data-id="element-4699" data-mp="2zfpkni2gmrua">
          Connects to everything
        </span>}
      <div className="relative w-full overflow-hidden" style={{
      WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 10%, #000 90%, transparent 100%)',
      maskImage: 'linear-gradient(to right, transparent 0, #000 10%, #000 90%, transparent 100%)'
    }} data-id="element-4700" data-mp="itau7mrm01x1">
        <motion.div className="flex gap-4 sm:gap-6 w-max" animate={reduceMotion ? undefined : {
        x: ['0%', '-50%']
      }} transition={reduceMotion ? undefined : {
        duration: 45,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-4701" data-mp="8wxykjci7dzn">
          {[...STRIP_LOGOS, ...STRIP_LOGOS].map((logo, i) => <StripLogo key={`${logo.simple}-${i}`} logo={logo} data-id="element-4702" data-mp="wqfxjezxumol" data-mpi={i} />)}
        </motion.div>
      </div>

      <WaitlistCTA to="/integrations" spinnerClassName={theme === 'light' ? 'text-white' : 'text-[#2B211A]'} className={`pointer-events-auto group mt-4 sm:mt-6 inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-bold shadow-lg transition-all duration-300 hover:scale-105 ${theme === 'light' ? 'bg-[#EA580C] text-white shadow-[#EA580C]/20 hover:bg-[#d24e09]' : 'bg-white text-[#2B211A] shadow-black/20 hover:bg-neutral-100'}`} data-id="element-4703" data-mp="rrv5i2uub9b8">
        See all integrations
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-4704" data-mp="34ybbqouv30fx" />
      </WaitlistCTA>
    </div>;
}
export function IntegrationSphere({
  brain,
  smoothProgress,
  active,
  reduceMotion,
  showCore = false,
  theme = 'dark'
}: {
  brain: {
    nodes: BrainNode[];
    edges: Edge[];
  };
  smoothProgress: MotionValue<number>;
  active: boolean;
  reduceMotion: boolean; // When true, the sphere renders its OWN centered Giga mark — the bright core
  // the mesh converges onto. It dissolves as the sphere zooms out (in step with
  // logoFade), so the web resolves to a clean field of white network dots.
  // Production <SlackIntegrationGraph> leaves this false because it owns a
  // separate "bridge" mark that morphs into the chat avatar; the card-morph
  // variant turns it on so the Home sphere has a Giga logo at its center.
  showCore?: boolean;
  theme?: 'dark' | 'light';
}) {
  // The animation frame state lives HERE so the rAF loop only re-renders this
  // sphere subtree every frame — never the heavy chat panel / page chrome.
  const [frame, setFrame] = useState({
    rot: 0,
    t: 0,
    p: 0
  });
  useEffect(() => {
    if (reduceMotion) {
      const unsub = smoothProgress.on('change', v => setFrame({
        rot: v * Math.PI * 1.2,
        t: 0,
        p: v
      }));
      return unsub;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = (now - start) / 1000;
      // Idle drift + spring-smoothed scroll contribution → fluid spin.
      const p = smoothProgress.get();
      const rot = t * 0.1 + p * Math.PI * 1.4;
      setFrame({
        rot,
        t,
        p
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion, smoothProgress]);
  const {
    rot,
    t,
    p
  } = frame;
  // As the sphere shrinks toward the chat hand-off (scroll ~0.42 -> 0.54), the
  // concrete brand-logo hubs dissolve into clean luminous orange/white nodes,
  // so the collapsing web reads as abstract "intelligence" condensing into the
  // Giga core rather than a shrinking wall of foreign app icons.
  // 0 = full logos, 1 = fully abstracted to glowing dots.
  const logoFade = Math.max(0, Math.min(1, (p - 0.42) / (0.54 - 0.42)));
  const {
    nodes,
    edges
  } = brain;
  // Project every node once per frame; edges + nodes both read from this.
  const projected = useMemo(() => nodes.map(node => project(node, rot, t)), [nodes, rot, t]);
  const flowing = active && !reduceMotion;
  return <div className="relative w-full max-w-[760px] aspect-square flex items-center justify-center" data-id="element-4705" data-mp="1nprxsn0k5n1s">
      {/* Warm brand bloom at the core that fades out with NO hard rim — so the
                                                                                                                                                                                                     web reads as Giga radiating OUTWARD into your stack, not boxed
                                                                                                                                                                                                     inside a sphere. (Containment ring removed: it made the graph
                                                                                                                                                                                                     feel like a cage rather than a network reaching out.) */}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
      width: RADIUS * 2.3,
      height: RADIUS * 2.3,
      // Cool, clean network glow — predominantly white with only a faint
      // warm core, so the web reads as a luminous system of nodes rather
      // than an orange brand bloom.
      background: theme === 'light' ? 'radial-gradient(circle, rgba(234,88,12,0.08) 0%, rgba(234,88,12,0.03) 26%, transparent 66%)' : 'radial-gradient(circle, rgba(255,255,255,0.20) 0%, rgba(255,237,222,0.10) 26%, rgba(255,255,255,0.05) 46%, rgba(20,14,9,0) 66%)'
    }} data-id="element-4706" data-mp="eorpopwnqm4j" />

      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
      overflow: 'visible'
    }} data-id="element-4707" data-mp="fbfgzglcz82s">
        {/* SYNAPSE MESH — every edge is a thin line whose brightness follows the
         average depth of its two endpoints, so the web fades to the back of
         the sphere and crowds bright at the front. */}
        {edges.map((edge, i) => {
        const A = projected[edge.a];
        const B = projected[edge.b];
        const front = (A.front + B.front) / 2;
        // Skip the very longest "wrap-around" struts on the deep back half so
        // the graph never draws lines straight across the empty middle.
        const span = Math.hypot(A.px - B.px, A.py - B.py);
        if (front < 0.32 && span > RADIUS * 0.9) return null;
        // Brighter white synapse lines so the mesh reads as a clean network
        // system of connections rather than a faint orange haze.
        const baseOpacity = 0.02 + front * front * 0.14;
        // A travelling pulse of light rides each short edge toward whichever
        // endpoint is closer to the center — the "thought firing" motion.
        let pulse: React.ReactNode = null;
        if (flowing && edge.short && front > 0.45) {
          const local = ((t / PULSE_CYCLE + edge.pulsePhase) % 1 + 1) % 1;
          // Travel from the outer endpoint toward the inner (center-ward) one.
          const aInner = Math.hypot(A.px, A.py) <= Math.hypot(B.px, B.py);
          const from = aInner ? B : A;
          const to = aInner ? A : B;
          const px = from.px + (to.px - from.px) * local;
          const py = from.py + (to.py - from.py) * local;
          const o = Math.sin(local * Math.PI) * (0.22 + front * 0.4);
          pulse = <>
                <circle cx={`calc(50% + ${px}px)`} cy={`calc(50% + ${py}px)`} r={3.4} fill="#FFFFFF" opacity={o * 0.32} data-id="element-4708" data-mp="1nl8vye7wss17" />

                <circle cx={`calc(50% + ${px}px)`} cy={`calc(50% + ${py}px)`} r={1.5} fill="#FFFFFF" opacity={o} data-id="element-4709" data-mp="1jlq93cwj26vq" />
              </>;
        }
        return <g key={`edge-${i}`} data-id="element-4710" data-mp="qwja6ct5vf9y" data-mpi={i}>
              <line x1={`calc(50% + ${A.px}px)`} y1={`calc(50% + ${A.py}px)`} x2={`calc(50% + ${B.px}px)`} y2={`calc(50% + ${B.py}px)`} stroke={theme === 'light' ? '#FFFFFF' : '#F5EFE9'} strokeWidth={theme === 'light' ? 1 : 0.7} strokeOpacity={theme === 'light' ? baseOpacity * 3.5 : baseOpacity} data-id="element-4711" data-mp="qxknknxngduu" />

              {pulse}
            </g>;
      })}

        {/* INBOUND BEAMS — bright brand-orange links along which packets of
         light travel FROM the front-facing hub nodes INTO the Giga core.
         This reads as Giga pulling context IN from all your tools (data
         flows toward Giga), rather than Giga emitting outward. */}
        {projected.map((P, i) => {
        const node = nodes[i];
        if (!node.hub || P.front < 0.55) return null;
        const reach = 0.35 + P.front * 0.4;
        let beamPulse: React.ReactNode = null;
        if (flowing) {
          // Each beam streams at its OWN pace, derived from the node's stable
          // phase, so the inbound traffic feels organic rather than mechanically
          // synchronized — some links pull context in faster than others.
          const nodeSpeed = 0.4 + node.twinklePhase * 7.13 % 1 * 0.42; // ~0.40–0.82
          // Two staggered packets per beam, the trailing one drifting a touch
          // slower, so even a single link reads as a living, uneven stream.
          const packets = [{
            offset: 0,
            speed: nodeSpeed
          }, {
            offset: 0.5,
            speed: nodeSpeed * 0.82
          }].map((cfg, k) => {
            // local goes 0 -> 1 = node -> core (data travelling INTO Giga).
            const local = ((t * cfg.speed + node.twinklePhase + cfg.offset) % 1 + 1) % 1;
            // Position interpolates from the node (local=0) toward the core
            // at center (local=1).
            const px = P.px * (1 - local);
            const py = P.py * (1 - local);
            // Brightest as it ARRIVES at the core, so the eye follows the
            // packet inward and the destination (Giga) gets the emphasis.
            const emit = local * local;
            const o = emit * (0.4 + P.front * 0.35);
            return <g key={k} data-id="element-4712" data-mp="3gsgwgx3tqzo7" data-mpi={k}>
                  <circle cx={`calc(50% + ${px}px)`} cy={`calc(50% + ${py}px)`} r={2.5 + local * 2.5} fill="#FFFFFF" opacity={o * 0.32} data-id="element-4713" data-mp="3gr6w219i1fee" />

                  <circle cx={`calc(50% + ${px}px)`} cy={`calc(50% + ${py}px)`} r={1.2 + local * 1} fill="#FFFFFF" opacity={o} data-id="element-4714" data-mp="2aa1un96xfq5f" />
                </g>;
          });
          beamPulse = <>{packets}</>;
        }
        return <g key={`beam-${i}`} data-id="element-4715" data-mp="kt6aheycvce8" data-mpi={i}>
              <line x1="50%" y1="50%" x2={`calc(50% + ${P.px}px)`} y2={`calc(50% + ${P.py}px)`} stroke={theme === 'light' ? '#FFFFFF' : '#F5EFE9'} strokeWidth={1} strokeOpacity={theme === 'light' ? reach * 0.55 : reach * 0.28} data-id="element-4716" data-mp="ptki4vw1okbq" />

              {beamPulse}
            </g>;
      })}

        {/* NODES — flat luminous dots. Size + brightness scale with depth so
         front dots feel close and back dots recede; hubs glow brand-orange
         like high-degree concept nodes in a knowledge graph. */}
        {projected.map((P, i) => {
        const node = nodes[i];
        const twinkle = 0.78 + Math.sin(t * 2 + node.twinklePhase * Math.PI * 2) * 0.18;
        const r = node.baseSize * (0.55 + P.front * 0.75);
        const opacity = (0.12 + P.front * 0.45) * twinkle;
        // The ambient web stays WHITE/cream for a calm neural look, but a
        // MINORITY of nodes are warm "live" accents that glow orange and
        // pulse a touch stronger — giving the web purposeful warmth and signs
        // of life without becoming a wall of orange. Orange also stays on the
        // meaningful signal (inbound data-beams + the core).
        const accent = !node.hub && i % 8 === 3;
        // Accents breathe with a stronger, slightly offset twinkle so they
        // read as living, glowing points rather than static dots.
        const accentTwinkle = 0.7 + Math.sin(t * 1.6 + node.twinklePhase * Math.PI * 2) * 0.3;
        const fill = node.hub ? '#FFFFFF' : accent ? theme === 'light' ? '#FFFFFF' : '#FDBA74' : theme === 'light' ? '#FFFFFF' : '#F4EEE8';
        // Show the brand logo on front-facing hubs, but only BEFORE the
        // shrink begins — once logoFade kicks in the logo fades to a dot.
        const showLogo = node.hub && node.logo && P.front > 0.62 && logoFade < 1;
        // A hub always carries its underlying luminous dot too; while the
        // logo is visible the dot sits hidden beneath it (faded by logoFade)
        // and then takes over completely as the logo dissolves.
        const hubDot = node.hub;
        const badge = 13 + P.front * 5;
        const dotR = node.hub ? r * 1.25 : r;
        const dotFill = node.hub ? theme === 'light' ? '#FFFFFF' : '#FFFFFF' : fill;
        const dotOpacity = node.hub ? (0.55 + P.front * 0.45) * twinkle : opacity;
        return <g key={`node-${i}`} data-id="element-4717" data-mp="dokhudkdi85m" data-mpi={i}>
              {node.hub && <>
                  {/* Warm outer bloom — makes the larger hub orbs (including
                             the ones drifting toward the silhouette) read as gently
                             lit rather than flat grey dots. */}
                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={r * 3.4} fill={theme === 'light' ? '#FFFFFF' : '#FB923C'} opacity={0.08 * P.front} data-id="element-4718" data-mp="9n8llp6zuoog" />

                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={r * 2.4} fill="#FFFFFF" opacity={0.06 * P.front} data-id="element-4719" data-mp="3vuyv0aatie2y" />
                </>}
              {/* Warm glow behind the accent ("live") nodes so they read as
                                   softly lit signals scattered through the calm white web. */}
              {accent && <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={r * 3.2} fill={theme === 'light' ? '#FFFFFF' : '#F97316'} opacity={0.16 * P.front * accentTwinkle} data-id="element-4720" data-mp="65w1gz3lf5ss" />}
              {showLogo && <g opacity={(0.5 + P.front * 0.5) * twinkle * (1 - logoFade)} data-id="element-4721" data-mp="3sf733369e21z">
                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={badge / 2 + 3} fill="#F97316" opacity={0.5} data-id="element-4722" data-mp="3nnf90inw9e73" />

                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={badge / 2} fill="#FFFFFF" data-id="element-4723" data-mp="3jmlj5jqw5pbk" />

                  <image href={`https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/${node.logo}.svg`} x={`calc(50% + ${P.px - badge * 0.32}px)`} y={`calc(50% + ${P.py - badge * 0.32}px)`} width={badge * 0.64} height={badge * 0.64} data-id="element-4724" data-mp="3ekwa29n8blq4" />
                </g>}
              {/* Luminous dot. For ordinary nodes this is the node. For hubs it
                                                                 is the orange/white synapse the logo dissolves INTO: it sits
                                                                 hidden behind the logo until the shrink begins (faded in by
                                                                 logoFade), then becomes the whole node. */}
              {hubDot ? <>
                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={dotR * 2.2} fill="#FFFFFF" opacity={0.22 * P.front * logoFade} data-id="element-4725" data-mp="2gt7zfs9y6dyj" />

                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={dotR} fill={dotFill} opacity={theme === 'light' ? dotOpacity * logoFade * 1.5 : dotOpacity * logoFade} data-id="element-4726" data-mp="2lr9jaff76hb2" />

                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={dotR * 0.45} fill="#FFF3E8" opacity={theme === 'light' ? dotOpacity * logoFade * 1.5 : dotOpacity * logoFade} data-id="element-4727" data-mp="1t3dojg96uzgx" />
                </> : accent ? <>
                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={r * 1.15} fill={fill} opacity={Math.min(1, (0.4 + P.front * 0.55) * accentTwinkle * (theme === 'light' ? 1.5 : 1))} data-id="element-4728" data-mp="16aq0uyzeydvk" />

                  <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={r * 0.5} fill={theme === 'light' ? '#FFFFFF' : '#FFF3E8'} opacity={Math.min(1, (0.4 + P.front * 0.55) * accentTwinkle * (theme === 'light' ? 1.5 : 1))} data-id="element-4729" data-mp="11dhjh0m3advd" />
                </> : <circle cx={`calc(50% + ${P.px}px)`} cy={`calc(50% + ${P.py}px)`} r={r} fill={fill} opacity={theme === 'light' ? opacity * 1.5 : opacity} data-id="element-4730" data-mp="sdl2zm9uslbe" />}
            </g>;
      })}
      </svg>

      {/* CENTER GIGA CORE — the bright mark the synapse mesh converges onto.
                                               Only rendered when `showCore` is set (the card-morph / Home variant);
                                               production <SlackIntegrationGraph> keeps it off and instead owns a
                                               separate parent "bridge" mark that morphs into the chat avatar. The
                                               core dissolves with logoFade so, as the sphere zooms out, it resolves
                                               into the clean field of tiny white network dots. */}
      {showCore && <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center" style={{
      opacity: 1 - logoFade
    }} data-id="element-4731" data-mp="3q9ua12cttstt">
          {/* Clean WHITE glow radiating from the core, so the center reads as
                                       the brightest node in the network system — not an orange brand
                                       bloom. */}
          <div className="absolute rounded-full" style={{
        width: 150,
        height: 150,
        background: theme === 'light' ? 'radial-gradient(circle, rgba(234,88,12,0.15) 0%, rgba(234,88,12,0.05) 26%, transparent 66%)' : 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.16) 45%, rgba(255,255,255,0) 72%)'
      }} data-id="element-4732" data-mp="rp2j1kgonlfv" />

          {/* The Giga mark on its own — no orange tile, just the crisp white
                                   glyph floating at the brightest point of the luminous web. */}
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className={`relative object-contain ${theme === 'light' ? '' : '[filter:brightness(0)_invert(1)] drop-shadow-[0_2px_12px_rgba(255,255,255,0.35)]'}`} style={{
        width: 44,
        height: 44
      }} data-id="element-4733" data-mp="3ubqbjm8pf7j3" />
        </div>}
    </div>;
}
export function SlackIntegrationGraph() {
  const reduceMotion = useReducedMotion();
  const brain = useMemo(() => buildBrain(), []);
  // Tall scroll track. The stage stays pinned while we scrub the choreography.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // On mobile the viewport is narrow, so the hard 2x sphere zoom crowds the
  // screen — pull back a touch so more of the radiating web is visible.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const {
    scrollYProgress
  } = useScroll({
    target: scrollRef,
    // Start tracking the moment the section's top reaches the BOTTOM of the
    // viewport (i.e. as soon as it scrolls into view), not when it pins. This
    // means the globe is already alive and forming as the section appears,
    // instead of sitting empty until the sticky pin engages.
    offset: ['start end', 'end end']
  });
  // A spring-smoothed copy of the scroll progress. The globe's rotation reads
  // THIS (not the raw scroll value) so fast or jittery scrolling becomes a
  // fluid, inertial spin instead of a 1:1 jump. We read it via .get() inside
  // the rAF loop, so rotation never depends on React re-renders.
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 26,
    mass: 0.5
  });
  // ── ACT ONE — SPHERE FIRST ────────────────────────────────────────────
  // The section opens on the dark integration sphere with the "Giga plugs into
  // your world." headline. As the user scrolls, the globe spins, then shrinks
  // and fades as we hand off to the chat act.
  // Headline-FIRST title card: the section opens on the big "Giga plugs into
  // your world." headline centered on the dark stage. It holds briefly, then
  // dissolves (fading + drifting up + shrinking) as the sphere takes over —
  // a clean "title → reveal" beat instead of a headline sitting over a live
  // globe the whole time.
  // The headline is its OWN clean beat. It stays hidden while the section is
  // still scrolling into view, FADES IN as the section locks (so the reveal is
  // a deliberate on-scroll moment, not something already faded by the time it
  // pins), HOLDS on its own with no sphere for a comfortable window, then
  // dissolves up as the sphere takes over.
  // The title is a clean, self-contained beat. It fades in as the section
  // locks, HOLDS on the empty dark stage, then fully clears (fades + lifts +
  // contracts) by 0.34 — BEFORE the sphere begins to appear — so the two
  // never overlap. The previous crossfade had the big white headline sitting
  // on top of the live sphere + logo strip + CTA all at once, which is what
  // read as cluttered/cheap. Now it's a deliberate hand-off, not a pile-up.
  const introOpacity = useTransform(scrollYProgress, [0, 0.08, 0.16, 0.28, 0.34], [0, 0, 1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0.08, 0.16, 0.28, 0.34], [24, 0, 0, -48]);
  const introScale = useTransform(scrollYProgress, [0.08, 0.16, 0.28, 0.34], [0.96, 1, 1, 0.92]);
  // The sphere MESH (synapse web, beams, hubs, logo strip) is its own beat. It
  // reveals after the headline leaves, holds, then DIES FULLY — dissolving in
  // place — before the lone Giga mark is handed to the chat. Crucially the mesh
  // finishes fading well before the mark begins travelling, so the "sphere"
  // visibly dies first and only the logo remains.
  // Sphere mesh enters as its OWN beat, AFTER the title has fully cleared
  // (title is gone by 0.34; the sphere starts at 0.36 so there's a beat of
  // clean stage between them, then it eases in to full by 0.46). It holds,
  // then dies in place before the lone Giga mark is handed to the chat.
  const sphereOpacity = useTransform(scrollYProgress, [0, 0.36, 0.46, 0.52, 0.58], [0, 0, 1, 1, 0]);
  // The sphere itself eases UP into place as it fades in — a subtle settle so
  // the web feels like it materializes deliberately rather than snapping on.
  const sphereEnterY = useTransform(scrollYProgress, [0.36, 0.46], [26, 0]);
  // The logo strip + CTA are the PROOF layer; they arrive a beat AFTER the
  // mesh has established (0.44 → 0.5) and a touch lower, so the scene builds
  // in sequence — mesh first, then the concrete tools settle in beneath it —
  // instead of every layer crossfading on at the same instant. They still
  // leave with the sphere so the whole act exits as one.
  const stripOpacity = useTransform(scrollYProgress, [0, 0.44, 0.5, 0.52, 0.58], [0, 0, 1, 1, 0]);
  const stripY = useTransform(scrollYProgress, [0.44, 0.5], [18, 0]);
  // Combined sphere Y = the fixed stage lift PLUS the subtle enter-settle, so
  // the wrapper reads a single motion value (hooks can't be called in JSX).
  const sphereYBase = typeof window !== 'undefined' ? window.innerHeight * 0.1 : 80;
  const sphereY = useTransform(sphereEnterY, v => -sphereYBase + v);
  // The sphere OPENS ZOOMED IN HARD on the web (cinematic crop), then visibly
  // SHRINKS as we scroll toward the chat hand-off — pulling back so the whole
  // radiating network collapses inward right as it dissolves into the chat
  // box. This shrink (rather than a flat hold) makes the "sphere → chat"
  // transition feel like the world condensing down into a single composer.
  // Mobile starts a touch tighter and shrinks the same way; desktop keeps the
  // larger, more dramatic pull-back.
  const sphereScale = useTransform(scrollYProgress, [0.34, 0.42, 0.58], isMobile ? [1.2, 0.92, 0.62] : [2.15, 1.55, 0.95]);
  // ── BACKGROUND: dark → cream. Warms to cream right as the sphere dissolves,
  // so there's no dead "lone logo on a dark stage" hold — the scene transitions
  // straight from the web into the chat. ──
  const bgOpacity = useTransform(scrollYProgress, [0.5, 0.64], [1, 0]);
  // ── ACT TWO — CHAT ────────────────────────────────────────────────────
  // The chat starts a little smaller, then grows and "docks" centered. A top
  // transform-origin + spring means it only ever grows DOWNWARD, letting its
  // bottom edge get cleanly cropped by the stage overflow — never cutting the
  // Giga reply at the top.
  // The chat is SUMMONED OUT OF the docked Giga mark: it stays hidden through
  // the mark's whole travel, then grows in (0.94 → 1 → 1.04) starting exactly
  // as the unit lands in the avatar slot, so the panel reads as being built
  // FROM the logo's position rather than cross-fading in behind a floating
  // card. The grow-in rides a snappier spring for a confident "lock into
  // place" feel.
  // Desktop now starts noticeably smaller (~25% down) and grows up past 1, so
  // the "lock into place" reads as more happening. Mobile keeps its original
  // near-1 range since it's already compact.
  const rawChatScale = useTransform(scrollYProgress, [0.72, 0.78, 0.9], isMobile ? [0.94, 1, 1.04] : [0.72, 0.92, 1.04]);
  const chatScale = useSpring(rawChatScale, {
    stiffness: 150,
    damping: 22,
    mass: 0.4
  });
  // CRITICAL: the panel must NOT be visible while the mark is still travelling,
  // or the big floating unit reads as a separate surface on top of an already-
  // built chat. So we hold it fully hidden until [0.72, 0.745] — the exact
  // window where the unit docks into the avatar and the chrome hands off — and
  // it resolves precisely as the logo locks into the avatar position.
  const chatOpacity = useTransform(scrollYProgress, [0.72, 0.745], [0, 1]);
  // The chat headline sits up top in Act Two; it fades in as the chat arrives.
  const chatHeadingOpacity = useTransform(scrollYProgress, [0.56, 0.66], [0, 1]);
  const chatHeadingY = useTransform(scrollYProgress, [0.56, 0.66], [16, 0]);
  // The globe owns its own animation loop now (see IntegrationSphere), so the
  // parent no longer re-renders every frame — only the sphere subtree does.
  const [active, setActive] = useState(false);
  // ── BRIDGE: centered Giga logo → chat avatar ──────────────────────────
  // As Act One hands off to Act Two, the big centered Giga core doesn't just
  // fade — it CONDENSES into a square and flies to where Giga's profile picture
  // sits in the chat reply, "landing" as the avatar. To land it precisely we
  // measure the real chat avatar (the panel already exposes a ref for it) and
  // tween a standalone mark from screen-center to that exact rect.
  const chatGigaRef = useRef<HTMLDivElement | null>(null);
  // Last-known on-screen rect of the chat's Giga avatar, in viewport px. The
  // sticky stage fills the viewport, so these map straight to absolute coords
  // inside the section. Falls back to a sensible estimate until first measured.
  const [avatarRect, setAvatarRect] = useState<{
    x: number;
    y: number;
    size: number;
  } | null>(null);
  useEffect(() => {
    if (reduceMotion) return;
    let raf = 0;
    const measure = () => {
      const el = chatGigaRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0) {
          setAvatarRect({
            x: r.left + r.width / 2,
            y: r.top + r.height / 2,
            size: r.width
          });
        }
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion]);
  // Estimated landing spot before the avatar is first measured: roughly where
  // the first Giga reply avatar sits inside the centered panel (left of the
  // conversation column, a little down from its top).
  const fallback = typeof window !== 'undefined' ? {
    x: window.innerWidth / 2 - 300,
    y: window.innerHeight * 0.46,
    size: 40
  } : {
    x: 0,
    y: 0,
    size: 40
  };
  const target = avatarRect ?? fallback;
  // ── THE SINGLE GIGA MARK ──────────────────────────────────────────────
  // There is exactly ONE Giga logo through this whole section. It is the bright
  // core the synapse mesh converges onto while the sphere is alive; then, with
  // no lone-logo hold, it immediately settles — gently shrinking, squaring off,
  // and gliding — into Giga's chat avatar as the chat fades in around it.
  // Because it's a single element the whole way, there is never a duplicate.
  // Lift the whole Act-One stage (spinning web + the centered Giga mark) a bit
  // above the viewport midline, so the core logo sits cleanly ABOVE the "Plugs
  // into your stack" label + logo strip instead of overlapping it. The same
  // lift is applied to the sphere wrapper (see render) so the web and the mark
  // stay perfectly aligned.
  const STAGE_LIFT = typeof window !== 'undefined' ? window.innerHeight * 0.1 : 80;
  const centerX = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
  const centerY = typeof window !== 'undefined' ? window.innerHeight / 2 - STAGE_LIFT : 0;
  // Apparent size of the core while the sphere holds (sphere wrapper sits at
  // ~2.0×, and the mark lives in un-scaled parent space, so we size it up to
  // read as the big glowing hub at the heart of the web).
  const CORE_SIZE = 150;
  // The handoff is staged into THREE deliberate beats:
  //
  //   BEAT 1 — MORPH + BOUNCE: the lone disc holds at center, then squares off
  //     into a rounded-square avatar shape with an energetic, springy "bubble"
  //     bounce (zoom in/out, never popping). This is TIME-BASED, not scroll-
  //     scrubbed — it fires once when scroll crosses the threshold and always
  //     plays at full energy, which is what makes it feel intentional and
  //     premium rather than mushy/amateur when the user scrolls slowly.
  //
  //   BEAT 2 — MAGNETIZE: once it has become the avatar shape, it glides into
  //     the exact slot where Giga's chat avatar sits — scroll-driven so the
  //     travel stays linked to the user's scroll for continuity.
  //
  //   BEAT 3 — LAND AS AVATAR: the mark settles in, the chat avatar fills with
  //     Giga's brand colour + logo at that instant, and only then does Giga's
  //     reply play. Before this, the avatar slot is a neutral grey placeholder.
  //
  // --- BEAT 1: the energetic morph, fired once on scroll threshold -----------
  // A boolean drives a time-based variant animation (see render), so the bounce
  // plays as a real, full-energy keyframe rather than being scrubbed by scroll.
  const [morphed, setMorphed] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    // Hysteresis: morph in at 0.52 (as the sphere is dissolving), allow it to
    // reset a little below so the beat can replay cleanly if the user scrolls
    // back up past the sphere.
    if (latest >= 0.52 && !morphed) setMorphed(true);else if (latest < 0.46 && morphed) setMorphed(false);
  });
  // --- BEAT 2: magnetize into the avatar slot --------------------------------
  // Held at 0 through the morph beat, then springs to the target. A snappier
  // spring (higher stiffness) gives the "magnetized into place" feel.
  const landProgress = useTransform(scrollYProgress, [0.6, 0.72], [0, 1]);
  const land = useSpring(landProgress, {
    stiffness: 130,
    damping: 20,
    mass: 0.7
  });
  // Interpolate the CENTER point, then offset by half the current size so the
  // mark stays centered on its target without mixing a separate translate (a
  // raw translateX/Y string would fight Framer's x/y on the same transform).
  const bridgeCx = useTransform(land, [0, 1], [centerX, target.x]);
  const bridgeCy = useTransform(land, [0, 1], [centerY, target.y]);
  const bridgeSize = useTransform(land, [0, 1], [CORE_SIZE, target.size]);
  const bridgeX = useTransform([bridgeCx, bridgeSize] as const, ([cx, s]: number[]) => cx - s / 2);
  const bridgeY = useTransform([bridgeCy, bridgeSize] as const, ([cy, s]: number[]) => cy - s / 2);
  // Inner logo padding scales with the box so the glyph keeps its proportions
  // as the mark shrinks from big core to small avatar.
  const bridgePadding = useTransform(bridgeSize, (s: number) => s * 0.26);
  // CLEAN HAND-OFF (no fade, no grey box, no double logo):
  // 1. The avatar slot stays COMPLETELY EMPTY while the mark travels — there is
  //    nothing in it (no grey placeholder, no outline) until the mark lands.
  // 2. The static chat avatar appears as a HARD CUT at the instant the mark is
  //    fully landed and pixel-aligned (over the razor-thin [0.728, 0.73] step),
  //    NOT a fade. The travelling mark then cuts out over [0.73, 0.735], one
  //    frame later, on top of the now-identical static avatar — so the eye
  //    never sees a fade, a grey box, or two logos.
  const chatAvatarReveal = useTransform(scrollYProgress, [0.728, 0.73], [0, 1]);
  const bridgeOpacity = useTransform(scrollYProgress, [0.34, 0.46, 0.73, 0.735], [0, 1, 1, 0]);
  // The chat conversation must not produce a RESPONSE until the logo is in
  // place. This flips true once the avatar has fully resolved; before it, the
  // panel holds in its "thinking" lead-in so results never appear behind a
  // grey, logo-less avatar.
  const [landed, setLanded] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (latest >= 0.735 && !landed) setLanded(true);else if (latest < 0.68 && landed) setLanded(false);
  });
  // ── HIDE THE GLOBAL FLOATING "Try Giga for free" CTA DURING THIS BEAT ──
  // The sphere / "See all integrations" act owns its own primary CTA, so we
  // suppress the competing global floating button while that beat is on screen
  // (sphere revealing → still holding through the logo-strip CTA), and release
  // it as the chat ("Lives inside your chats") beat takes over. The window is
  // tied to the same progress the sphere + its CTA use, with a little
  // hysteresis so it doesn't flicker on slow scrolls.
  // Suppress for the ENTIRE window the "See all integrations" pill is on screen.
  // The pill rides `sphereOpacity` (ramps in ~0.40, holds 0.46–0.52, fades out
  // by 0.58), so we keep the global CTA hidden across that whole span — plus a
  // little lead/lag hysteresis — so the two never appear together. We engage a
  // touch BEFORE the pill ramps in and release a touch AFTER it has fully gone,
  // guaranteeing only one CTA is ever visible at a time.
  const [inIntegrationBeat, setInIntegrationBeat] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (!inIntegrationBeat && latest >= 0.32 && latest < 0.6) {
      setInIntegrationBeat(true);
    } else if (inIntegrationBeat && (latest >= 0.64 || latest < 0.28)) {
      setInIntegrationBeat(false);
    }
  });
  // Under reduced motion the choreography is collapsed, so never suppress.
  useSuppressFloatingCta(!reduceMotion && inIntegrationBeat);
  // A soft warm halo behind the mark, strongest while it's the live core and
  // during the bubble beat, easing off as it magnetizes into the avatar.
  const bridgeGlow = useTransform(land, [0, 0.6, 1], [0.55, 0.4, 0]);
  return <div ref={scrollRef} className="relative w-full" style={{
    height: reduceMotion ? undefined : '300vh'
  }} data-id="element-4734" data-mp="25koj15yqik96">
      {/* Sticky stage — pinned while the user scrolls the tall track. The base
                                                                                                                                                                                                                                                                         is the brand cream (Act Two); a dark scene sits on top for Act One
                                                                                                                                                                                                                                                                         and fades out to reveal the cream chat stage as the user scrolls. */}
      <section className="sticky top-0 h-screen w-full overflow-hidden flex flex-col bg-[#FAF8F4]" data-id="element-4735" data-mp="2z3hhmtjnt0n4">
        {/* Dark scene layer — present at the start (Act One), fades out. Under
         reduced motion we collapse to the white chat stage, so it's hidden. */}
        {/* Rich, deep base — a vertical roast-to-espresso gradient instead of a
         flat brown, so the scene already has dimension before anything else. */}
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        background: 'linear-gradient(180deg, #271D17 0%, #2B201A 50%, #241A14 100%)'
      }} data-id="element-4736" data-mp="ngxl7anvrgf5" />

        {/* (Brand glow + vignette removed — the scene is now a clean flat brown
         base with grain only, no centered bloom or corner darkening.) */}
        {/* Fine film grain for tactile, printed-matter depth. */}
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.45'/%3E%3C/svg%3E\")",
        mixBlendMode: 'soft-light'
      }} data-id="element-4737" data-mp="16e8jarxs03t0" />

        {/* Readability scrim behind the top headline so white copy always reads,
         even when a bright front-facing logo rotates up near it. */}
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[34%]" style={{
        opacity: reduceMotion ? 0 : introOpacity,
        background: 'linear-gradient(to bottom, rgba(20,14,9,0.55), rgba(20,14,9,0) 100%)'
      }} data-id="element-4738" data-mp="289lauilkxjii" />

        {/* ACT ONE — big centered title card. Opens prominent and centered on
         the dark stage, then dissolves up as the sphere reveals itself. */}
        <motion.div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6" style={{
        opacity: reduceMotion ? 0 : introOpacity,
        y: reduceMotion ? 0 : introY,
        scale: reduceMotion ? 1 : introScale
      }} data-id="element-4739" data-mp="1pcbrf1g98619">
          <h2 className="max-w-4xl text-4xl sm:text-6xl md:text-7xl font-title font-medium tracking-tight leading-[1.05] text-white" data-id="element-4740" data-mp="18395a3vvdm6d">
            Giga plugs into your world.
          </h2>
        </motion.div>

        {/* ACT ONE — the integration sphere, centered and spinning. Lifted by
         STAGE_LIFT so its core sits above the logo strip, matching the
         centered Giga mark's start position. */}
        <motion.div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" onViewportEnter={() => setActive(true)} style={{
        opacity: reduceMotion ? 0 : sphereOpacity,
        scale: reduceMotion ? 1 : sphereScale,
        y: reduceMotion ? 0 : sphereY
      }} data-id="element-4741" data-mp="1rovqafefpeo6">
          <IntegrationSphere brain={brain} smoothProgress={smoothProgress} active={active || !!reduceMotion} reduceMotion={!!reduceMotion} data-id="element-4742" data-mp="1vt3embgzcslv" />
        </motion.div>

        {/* ACT ONE — brand-logo strip beneath the sphere. The neural web is the
         metaphor; this marquee is the concrete proof of which tools plug
         into Giga. Tied to the sphere's opacity so they enter/leave as one
         beat. */}
        <motion.div className="pointer-events-none absolute inset-x-0 bottom-[16%] sm:bottom-[15%] z-30 flex justify-center px-6" style={{
        opacity: reduceMotion ? 0 : stripOpacity,
        y: reduceMotion ? 0 : stripY
      }} data-id="element-4743" data-mp="7wm38oqp7e30">
          <LogoStrip reduceMotion={!!reduceMotion} data-id="element-4744" data-mp="3gauqried5eob" />
        </motion.div>

        {/* ACT TWO — chat headline pinned to the top once the chat arrives. */}
        <motion.div className="pointer-events-none absolute top-[8%] sm:top-[10%] left-0 right-0 z-30 text-center px-6" style={{
        opacity: reduceMotion ? 1 : chatHeadingOpacity,
        y: reduceMotion ? 0 : chatHeadingY
      }} data-id="element-4745" data-mp="3l7cucexyj6z2">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-3 md:mb-4" data-id="element-4746" data-mp="2xkd2bq0kd58r">
            Right where you work
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-title font-medium tracking-tight text-[#2B211A]" data-id="element-4747" data-mp="32gv36esrwjft">
            Lives inside your chats.
          </h2>
        </motion.div>

        {/* ACT TWO — chat layer, anchored to the TOP of the stage so it only
         grows downward and its bottom edge gets cropped, never the reply.
         On mobile it sits a touch higher and the panel is shorter so the
         whole composer fits within the pinned stage with breathing room —
         it never reads as spilling onto the section below. */}
        <motion.div className="absolute inset-x-0 top-[23%] sm:top-[28%] z-30 flex justify-center px-6 sm:px-4" style={{
        transformOrigin: 'center top',
        scale: reduceMotion ? 1 : chatScale,
        opacity: reduceMotion ? 1 : chatOpacity
      }} data-id="element-4748" data-mp="279ktu115mcmy">
          <SlackPanel gigaRef={chatGigaRef} avatarReveal={reduceMotion ? 1 : chatAvatarReveal} responseReady={reduceMotion ? true : landed} data-id="element-4749" data-mp="1sck8cf9k3dwx" />
        </motion.div>

        {/* THE SINGLE GIGA MARK — it is the live core of the sphere, then the
         lone logo held on the dark stage as the mesh dies, then it settles
         into the chat's Giga avatar. One element the whole way → never a
         duplicate. Hidden under reduced motion (no choreography to play). */}
        {!reduceMotion && <motion.div aria-hidden="true" className="pointer-events-none absolute left-0 top-0 z-[45]" style={{
        width: bridgeSize,
        height: bridgeSize,
        x: bridgeX,
        y: bridgeY,
        opacity: bridgeOpacity
      }} data-id="element-4750" data-mp="14rjec0bbhist">
            {/* Warm halo — reads as the glowing heart of the network, eases out
             as the mark shrinks into the avatar. */}
            <motion.span aria-hidden="true" className="pointer-events-none absolute -inset-[35%] rounded-full" style={{
          opacity: bridgeGlow,
          background: 'radial-gradient(circle, rgba(234,88,12,0.55) 0%, rgba(234,88,12,0.18) 38%, rgba(234,88,12,0) 70%)'
        }} data-id="element-4751" data-mp="1nnsq2gyqxx9n" />

            {/* The mark box. The circle→rounded-square MORPH plays here as a
             single TIME-BASED beat triggered by `morphed`. The shape eases
             from disc to avatar on one confident spring, with a SINGLE,
             barely-there scale breath (1 → 1.04 → 1) — one clean motion, no
             repeated zoom in/out, so it reads premium and unhurried rather
             than trying too hard. */}
            <motion.div className="relative flex h-full w-full items-center justify-center bg-[#EA580C] shadow-[0_8px_30px_rgba(234,88,12,0.45)]" style={{
          padding: bridgePadding
        }} initial={false} animate={morphed ? {
          borderRadius: '24%',
          scale: [1, 1.04, 1]
        } : {
          borderRadius: '50%',
          scale: 1
        }} transition={morphed ? {
          borderRadius: {
            type: 'spring',
            stiffness: 200,
            damping: 26,
            mass: 0.8
          },
          scale: {
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.45, 1]
          }
        } : {
          duration: 0.3,
          ease: 'easeOut'
        }} data-id="element-4752" data-mp="2zb1p29aj0fg1">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" className="w-full h-full object-contain [filter:brightness(0)_invert(1)]" data-id="element-4753" data-mp="1u9a95cm0p4c8" />
            </motion.div>
          </motion.div>}

        {/* Scroll hint — only during Act One. A bouncing chevron-down reads as a
         clear affordance; the label sits quietly above it. */}
        <motion.div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1.5 text-white/50" style={{
        opacity: reduceMotion ? 0 : introOpacity
      }} data-id="element-4754" data-mp="2dw7ybp1xw6lo">
          <span className="text-[11px] font-semibold uppercase tracking-[3px]" data-id="element-4755" data-mp="2mwd3dsjnuswo">
            Scroll
          </span>
          <motion.span animate={{
          y: [0, 6, 0]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-4756" data-mp="skg97rtu38xx">
            <ChevronDown size={18} strokeWidth={2.25} data-id="element-4757" data-mp="l39w30d2pzm1" />
          </motion.span>
        </motion.div>
      </section>
    </div>;
}